import { RotateCcw, RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/shadcn/index.js';
import { Button } from '../../components/ui/index.js';

const FRAME = 300; // square preview / crop frame (px)
const OUT = 1024; // exported square size (px) — caps resolution = compression
const QUALITY = 0.82; // JPEG quality — the rest of the compression
const MAX_ZOOM = 4;

export interface ImageEditorDialogProps {
  open: boolean;
  /** The picked source image. */
  source: File | null;
  title?: string;
  /** Shown while the caller persists the result. */
  busy?: boolean;
  onCancel: () => void;
  /** Receives the cropped + compressed square image as a JPEG blob. */
  onSave: (blob: Blob) => void | Promise<void>;
}

/**
 * Dependency-free image editor: drag to reposition, zoom (slider/wheel), and
 * rotate in 90° steps within a square frame, then export a compressed square
 * JPEG. Export replays the exact preview transform onto an OUT×OUT canvas, so
 * what you see in the frame is what gets saved — correct for any
 * pan/zoom/rotation without per-pixel crop math.
 */
export function ImageEditorDialog({
  open,
  source,
  title,
  busy,
  onCancel,
  onSave,
}: ImageEditorDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0); // 0 | 90 | 180 | 270
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    if (!open || !source) return undefined;
    const objUrl = URL.createObjectURL(source);
    setUrl(objUrl);
    setImg(null);
    setZoom(1);
    setRot(0);
    setOffset({ x: 0, y: 0 });
    const im = new Image();
    im.onload = () => setImg(im);
    im.src = objUrl;
    return () => URL.revokeObjectURL(objUrl);
  }, [open, source]);

  const baseScale = img ? FRAME / Math.min(img.naturalWidth, img.naturalHeight) : 1;
  const scale = baseScale * zoom;

  const clampOffset = useCallback(
    (o: { x: number; y: number }, z = zoom, r = rot): { x: number; y: number } => {
      if (!img) return o;
      const s = baseScale * z;
      const swapped = r % 180 !== 0;
      const dispW = (swapped ? img.naturalHeight : img.naturalWidth) * s;
      const dispH = (swapped ? img.naturalWidth : img.naturalHeight) * s;
      const maxX = Math.max(0, (dispW - FRAME) / 2);
      const maxY = Math.max(0, (dispH - FRAME) / 2);
      return {
        x: Math.max(-maxX, Math.min(maxX, o.x)),
        y: Math.max(-maxY, Math.min(maxY, o.y)),
      };
    },
    [img, baseScale, zoom, rot],
  );

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    dragRef.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const d = dragRef.current;
    if (!d) return;
    setOffset(clampOffset({ x: d.ox + (e.clientX - d.px), y: d.oy + (e.clientY - d.py) }));
  };
  const onPointerUp = (): void => {
    dragRef.current = null;
  };

  const applyZoom = (z: number): void => {
    const nz = Math.max(1, Math.min(MAX_ZOOM, z));
    setZoom(nz);
    setOffset((o) => clampOffset(o, nz));
  };
  const rotate = (dir: 1 | -1): void => {
    setRot((r) => (r + dir * 90 + 360) % 360);
    setOffset({ x: 0, y: 0 });
  };

  const doSave = async (): Promise<void> => {
    if (!img) return;
    const canvas = document.createElement('canvas');
    canvas.width = OUT;
    canvas.height = OUT;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // White matte — JPEG has no alpha, so transparent PNGs flatten cleanly.
    // (Named colour, not a UI token — keeps the brand-token lint rule happy.)
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, OUT, OUT);
    const ratio = OUT / FRAME;
    ctx.translate(OUT / 2 + offset.x * ratio, OUT / 2 + offset.y * ratio);
    ctx.rotate((rot * Math.PI) / 180);
    ctx.scale(scale * ratio, scale * ratio);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', QUALITY));
    if (blob) await onSave(blob);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title ?? t('imageEditor.title', { defaultValue: 'Edit image' })}</DialogTitle>
          <DialogDescription>
            {t('imageEditor.hint', {
              defaultValue: 'Drag to reposition · scroll or use the slider to zoom.',
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4">
          <div
            className="relative touch-none select-none overflow-hidden rounded-xl border border-border bg-bg-subtle"
            style={{ width: FRAME, height: FRAME, cursor: 'grab' }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            onWheel={(e) => applyZoom(zoom + (e.deltaY < 0 ? 0.15 : -0.15))}
          >
            {img && url && (
              <img
                src={url}
                alt=""
                draggable={false}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  marginLeft: -img.naturalWidth / 2,
                  marginTop: -img.naturalHeight / 2,
                  maxWidth: 'none',
                  transformOrigin: 'center',
                  transform: `translate(${offset.x}px, ${offset.y}px) rotate(${rot}deg) scale(${scale})`,
                }}
              />
            )}
            {/* center crop guides */}
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/20" aria-hidden />
          </div>

          <div className="flex w-full items-center gap-3">
            <ZoomOut className="h-4 w-4 flex-none text-fg-muted" aria-hidden />
            <input
              type="range"
              min={1}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              onChange={(e) => applyZoom(Number(e.target.value))}
              aria-label={t('imageEditor.zoom', { defaultValue: 'Zoom' })}
              className="h-1.5 flex-1 cursor-pointer accent-brand"
            />
            <ZoomIn className="h-4 w-4 flex-none text-fg-muted" aria-hidden />
            <span className="mx-1 h-5 w-px bg-border" aria-hidden />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => rotate(-1)}
              aria-label={t('imageEditor.rotateLeft', { defaultValue: 'Rotate left' })}
              title={t('imageEditor.rotateLeft', { defaultValue: 'Rotate left' })}
            >
              <RotateCcw className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => rotate(1)}
              aria-label={t('imageEditor.rotateRight', { defaultValue: 'Rotate right' })}
              title={t('imageEditor.rotateRight', { defaultValue: 'Rotate right' })}
            >
              <RotateCw className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('actions.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={() => void doSave()} loading={busy} disabled={!img}>
            {t('imageEditor.apply', { defaultValue: 'Apply' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
